import React, { useMemo } from 'react'
import {
  Box,
  Pagination as MuiPagination,
  useMediaQuery,
  useTheme,
  Typography,
  styled,
} from '@mui/material'

// Custom styled pagination component
const StyledPagination = styled(MuiPagination)(({ theme }) => ({
  // Container styles
  '& .MuiPagination-ul': {
    gap: '4px',
  },
  // Style for all items
  '& .MuiPaginationItem-root': {
    borderRadius: '8px',
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    border: 'none',
    color: '#232536',
    minWidth: '36px',
    height: '36px',
    '&:hover': {
      backgroundColor: 'rgba(255, 207, 81, 0.12)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
    },
  },
  // Selected page styles
  '& .Mui-selected': {
    backgroundColor: '#232536 !important',
    color: 'white !important',
    fontWeight: 700,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      backgroundColor: '#232536 !important',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
    },
  },
  // Previous and next buttons
  '& .MuiPaginationItem-previousNext': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#ffcf51',
      borderColor: '#ffcf51',
      color: '#232536',
    },
  },
  // First and last buttons
  '& .MuiPaginationItem-firstLast': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#ffcf51',
      borderColor: '#ffcf51',
      color: '#232536',
    },
  },
  // Disabled buttons
  '& .Mui-disabled': {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      transform: 'none',
      boxShadow: 'none',
    },
  },
}))

// Animated wrapper for the entire pagination section
const AnimatedPaginationWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '32px',
  marginBottom: '24px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-16px',
    left: '5%',
    width: '90%',
    height: '1px',
    backgroundColor: '#e0e0e0',
  },
})

const Pagination = ({ itemsPerPage, totalItems, currentPage, paginate }) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))

  const totalPages = useMemo(
    () => Math.ceil(totalItems / itemsPerPage),
    [totalItems, itemsPerPage]
  )
// Don't render pagination if there's only one page
  // if (totalPages <= 1) return null

  // Determine how many pages to show on smaller screens
  const siblingCount = isSmallScreen ? 0 : isMediumScreen ? 1 : 1

  // Create page description text
  const paginationText = `Page ${currentPage} of ${totalPages}`

  return (
    <AnimatedPaginationWrapper>
      <StyledPagination
        count={totalPages}
        page={currentPage}
        onChange={(event, page) => {
          paginate(page)
          // Optional: Scroll to top when changing pages
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }}
        siblingCount={siblingCount}
        size={isSmallScreen ? 'small' : 'medium'}
        showFirstButton={!isSmallScreen}
        showLastButton={!isSmallScreen}
        boundaryCount={isSmallScreen ? 0 : 1}
      />
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          mt: 1.5,
          fontWeight: 500,
          fontSize: isSmallScreen ? '0.75rem' : '0.875rem',
        }}
      >
        {paginationText}
      </Typography>
    </AnimatedPaginationWrapper>
  )
}

export default Pagination